import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Activity, AlertTriangle, CircleAlert, Bot, Loader2, Sparkles, Info, Wrench } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Same target-AFR map as TuneScorecard so the two paths produce the same
// derived tune. Keep in sync with /app/frontend/src/components/TuneScorecard.jsx.
const TARGET_AFR_BY_VERSION = {
  V9: 11.5, V10: 11.5, V11: 11.5, V11_BestOfBoth: 11.5,
  V12_SAFE: 11.0, V13_ALPHAN: 10.5,
};
const VERSION_TO_FILENAME = {
  V9: 'BMW_E90_328xi_E85_V9.bin',
  V10: 'BMW_E90_328xi_E85_V10.bin',
  V11: 'BMW_E90_328xi_E85_V11.bin',
  V11_BestOfBoth: 'BMW_E90_328xi_E85_V11.bin',
  V12_SAFE: 'BMW_E90_328xi_E85_V12_SAFE.bin',
  V13_ALPHAN: 'BMW_E90_328xi_E85_V13_AlphaN.bin',
};

// Inline error boundary — keeps a panel-level failure from nuking the entire
// analysis view. Root cause for the production `DataCloneError` was either a
// library-level clone op (recharts/react-markdown deep within React's
// concurrent render) or an object-as-child render. We now contain any such
// failure to the child panel and let the rest of the analysis show.
class PanelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || String(error) };
  }
  componentDidCatch(error, info) {
    // Surface in dev; silent in prod.
    if (process.env.NODE_ENV === 'development') {
      console.error('[DatalogAnalysisView panel]', error, info);
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <Card className="bg-[#0A0A0A] border-red-500/30 rounded-sm" data-testid="panel-error-boundary">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm text-white font-medium">{this.props.label || 'Panel failed to render'}</p>
                <p className="text-xs text-zinc-500 mt-1 break-words">{this.state.message}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

// Helpers — defend against the many shapes the datalog response takes.
const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
const fmt = (v, digits = 2, fallback = 'N/A') => {
  const n = num(v);
  return n === null ? fallback : n.toFixed(digits);
};

// Normalize an `issue` — backend returns dicts with `{type, message, severity}`,
// older code paths return plain strings. Rendering a dict as a React child
// throws "Objects are not valid as a React child" which in concurrent mode
// can surface as a cascading clone/commit failure.
const issueText = (issue) => {
  if (!issue) return '';
  if (typeof issue === 'string') return issue;
  return issue.message || issue.type || JSON.stringify(issue);
};
const issueKey = (issue, idx) => {
  if (typeof issue === 'string') return `${idx}-${issue}`;
  return `${idx}-${issue?.type || issue?.message || 'issue'}`;
};

function AIInsightsPanel({ datalog }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Track which datalog we've already auto-analysed so a re-render doesn't refire it.
  const autoRunFor = useRef(null);

  // Detect a CSV that parsed to no usable data — all-zero RPM/AFR rows, missing
  // raw_stats, or sample_rows that are all zeros. Without this check the auto-AI
  // fires Claude with a zero'd summary, which then collides with the system-context
  // pulling the user's *older* valid log → confusing "two conflicting datasets" output.
  const isCsvEmpty = useCallback((d) => {
    if (!d) return true;
    const rawStats = d.raw_stats || {};
    const a = d.analysis || {};
    const rpmMax = rawStats?.rpm?.max ?? a?.max_rpm ?? 0;
    const afrAvg = rawStats?.afr_b1?.avg ?? a?.avg_afr ?? 0;
    const total = d.data_points ?? a?.total_rows ?? 0;
    const sample = Array.isArray(d.sample_rows) ? d.sample_rows : [];
    const sampleAllZero = sample.length > 0 && sample.every((r) =>
      (!r.rpm || r.rpm === 0) && (!r.afr || r.afr === 0)
    );
    return total < 2 || ((!rpmMax || rpmMax < 100) && (!afrAvg || afrAvg < 1)) || sampleAllZero;
  }, []);

  const getInsights = useCallback(async () => {
    if (isCsvEmpty(datalog)) {
      setError('This datalog parsed to no usable rows (RPM/AFR all zero). Re-export from your logger and re-upload — sending zeros to the AI will confuse it with your previous valid log.');
      return;
    }
    setLoading(true);
    setError(null);
    const analysis = datalog?.analysis || {};
    const filename = datalog?.filename || 'datalog.csv';
    const rawStats = datalog?.raw_stats || {};
    const scorecard = datalog?.verdict?.scorecard;
    const healthGate = datalog?.verdict?.health_gate;
    const curve = datalog?.verdict?.curve;

    // Raw diagnostic rows — the missing piece that caused the AI to say "I cannot
    // see the raw CSV rows". Backend extracts ~12 most diagnostic rows per upload.
    const sampleRows = Array.isArray(datalog?.sample_rows) ? datalog.sample_rows : [];
    const sampleRowsText = sampleRows.length
      ? 'Diagnostic Row Samples (from the raw CSV, chronological order):\n' +
        sampleRows.map((r) =>
          `  [${r.label}] idx=${r.idx} RPM=${r.rpm} AFR=${r.afr} Timing=${r.timing}° ` +
          `Load=${r.load}% TQ=${r.torque} Pedal=${r.pedal}% Knock=${r.knock}V`
        ).join('\n')
      : '';

    // Pull real numbers from the actual backend shape (raw_stats / analysis.wot / analysis.hp).
    const dataPoints = datalog?.data_points ?? analysis?.total_rows;
    const durationSec = datalog?.duration_seconds ?? analysis?.duration_sec;
    const maxRpm = rawStats?.rpm?.max ?? analysis?.max_rpm;
    const avgAfr = rawStats?.afr_b1?.avg ?? analysis?.avg_afr;
    const hp = analysis?.hp || analysis?.hp_estimate || {};
    const wot = analysis?.wot || analysis?.wot_analysis || {};
    const issues = Array.isArray(datalog?.issues) ? datalog.issues
      : Array.isArray(analysis?.issues) ? analysis.issues : [];
    const recs = Array.isArray(datalog?.recommendations) ? datalog.recommendations
      : Array.isArray(analysis?.recommendations) ? analysis.recommendations : [];

    const wotLines = Object.entries(wot).map(([range, d]) =>
      `  ${range}: AFR ${fmt(d?.afr)} / Timing ${fmt(d?.timing, 1)}° / Torque ${fmt(d?.torque, 1)} (${d?.afr_status || d?.status || '—'})`
    );

    const summary = [
      `Datalog: ${filename}`,
      `Rows: ${dataPoints ?? 'N/A'}, Duration: ${fmt(durationSec, 0)}s`,
      datalog?.tune_version ? `Tune Version (auto-detected from "${datalog?.detected_from || 'filename'}"): ${datalog.tune_version}` : '',
      scorecard?.verdict ? `Scorecard Verdict: ${scorecard.verdict}${scorecard.score ? ` (score ${scorecard.score})` : ''}` : '',
      Array.isArray(scorecard?.notes) && scorecard.notes.length ? `Scorecard Notes: ${scorecard.notes.join('; ')}` : '',
      healthGate?.verdict ? `Mechanical Health: ${healthGate.verdict}` : '',
      Array.isArray(healthGate?.issues) && healthGate.issues.length ? `Health Issues: ${healthGate.issues.map(issueText).join('; ')}` : '',
      num(curve?.peak_hp) !== null ? `Verdict Peak HP: ${fmt(curve.peak_hp, 1)} @ ${curve?.peak_hp_rpm || '?'} RPM` : '',
      num(curve?.peak_iat_f) !== null ? `Peak IAT: ${fmt(curve.peak_iat_f, 0)}°F` : '',
      num(maxRpm) !== null ? `Max RPM: ${fmt(maxRpm, 0)}` : '',
      num(avgAfr) !== null ? `Avg AFR: ${fmt(avgAfr)}` : '',
      num(hp?.estimated_peak_hp ?? hp?.peak_hp) !== null ? `Est HP: ${fmt(hp.estimated_peak_hp ?? hp.peak_hp, 1)} @ ${fmt(hp?.peak_hp_rpm, 0)} RPM` : '',
      num(hp?.estimated_peak_torque ?? hp?.peak_torque) !== null ? `Est Torque: ${fmt(hp.estimated_peak_torque ?? hp.peak_torque, 1)} ft-lbs` : '',
      wotLines.length ? `WOT Bands:\n${wotLines.join('\n')}` : '',
      recs.length ? `Recommendations: ${recs.join('; ')}` : '',
      issues.length ? `Issues: ${issues.map(issueText).join('; ')}` : '',
      sampleRowsText,
    ].filter(Boolean).join('\n');

    try {
      const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message:
            `I just uploaded this datalog. Analyze it the way you would if I pasted the CSV into chat — ` +
            `reference the detected tune version, call out what the scorecard + health-gate say, highlight ` +
            `any IAT/AFR/timing concerns, and give me the single most important next action. Be specific ` +
            `and direct.\n\n${summary}`,
          mode: 'advise',
        }),
      });
      if (!res.ok) throw new Error('AI service unavailable');
      const data = await res.json();
      setInsights(data.response);
    } catch (err) {
      setError(err?.message || 'AI service unavailable');
    }
    setLoading(false);
  }, [datalog]);

  // Auto-run AI analysis right after a fresh upload. App.js sets `_auto_ai: true`
  // on the datalog object it passes in so the chat-style narrative appears without
  // requiring the user to click "Get AI Insights".
  useEffect(() => {
    if (!datalog?._auto_ai) return;
    const key = datalog.id || datalog.filename;
    if (autoRunFor.current === key) return;
    autoRunFor.current = key;
    if (isCsvEmpty(datalog)) {
      // Don't auto-fire Claude on an empty/corrupted log — would conflict with system context's previous valid log
      setError('This datalog parsed to no usable rows (RPM/AFR all zero). Re-export from your logger (CSV with a real WOT pull) and upload again. Auto-AI skipped to avoid confusing context.');
      return;
    }
    getInsights();
  }, [datalog, getInsights, isCsvEmpty]);

  return (
    <Card className="bg-[#0A0A0A] border-[#FF5E1A]/20 rounded-sm" data-testid="ai-insights-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit'] text-base">
          <Bot className="w-5 h-5 text-[#FF5E1A]" />AI Tuning Insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!insights && !loading && !error && (
          <div className="text-center py-6">
            <Sparkles className="w-8 h-8 text-[#FF5E1A]/40 mx-auto mb-3" />
            <p className="text-sm text-zinc-400 mb-4">AI narrative analysis — same depth as pasting the CSV into chat. Runs automatically on fresh uploads.</p>
            <Button onClick={getInsights} className="bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm" data-testid="get-ai-insights-btn">
              <Bot className="w-4 h-4 mr-2" />Analyze Now
            </Button>
          </div>
        )}
        {loading && (
          <div className="flex items-center gap-3 py-6 justify-center" data-testid="ai-insights-loading">
            <Loader2 className="w-5 h-5 text-[#FF5E1A] animate-spin" />
            <span className="text-sm text-zinc-400">Claude is analyzing your datalog...</span>
          </div>
        )}
        {error && (
          <div className="text-center py-4" data-testid="ai-insights-error">
            <p className="text-sm text-red-400 mb-3">{error}</p>
            <Button onClick={getInsights} variant="outline" className="border-[#FF5E1A]/30 text-[#FF5E1A] rounded-sm text-xs">Retry</Button>
          </div>
        )}
        {insights && (
          <div className="prose prose-invert prose-sm max-w-none
            prose-headings:font-['Outfit'] prose-headings:text-white
            prose-code:text-[#FF5E1A] prose-code:bg-[#1A1A1A] prose-code:px-1 prose-code:py-0.5 prose-code:rounded-sm prose-code:text-xs prose-code:font-['JetBrains_Mono']
            prose-pre:bg-[#050505] prose-pre:border prose-pre:border-[#27272A] prose-pre:rounded-sm
            prose-strong:text-white prose-li:text-zinc-300 prose-p:text-zinc-300" data-testid="ai-insights-content">
            <ReactMarkdown>{insights}</ReactMarkdown>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value, unit }) {
  return (
    <div className="bg-[#050505] border border-[#27272A] rounded-sm p-3">
      <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-xl font-bold text-white font-['JetBrains_Mono']">
        {value} {unit && <span className="text-xs text-zinc-600 font-normal">{unit}</span>}
      </p>
    </div>
  );
}

function AFRStatusBadge({ status }) {
  const colors = {
    RICH: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    LEAN: 'bg-red-500/20 text-red-400 border-red-500/30',
    NORMAL: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    OK: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    TOO_LEAN: 'bg-red-500/20 text-red-400 border-red-500/30',
    TOO_RICH: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  };
  const cls = colors[status] || colors.NORMAL;
  return <span className={`text-xs px-2 py-0.5 border rounded-sm font-bold ${cls}`}>{status || '—'}</span>;
}

function getAFRBarColor(afr) {
  if (afr > 15) return 'bg-red-500';
  if (afr > 13) return 'bg-yellow-500';
  return 'bg-emerald-500';
}

// RevisionTuneCTA — single-click "Generate Revision Tune" panel that fires
// when the analysis has enough signal to derive a new bin. Dispatches a
// window CustomEvent that the My Tunes tab + TuneTimeline subscribe to, so
// the user lands on a pre-filled Derive Tune dialog without re-typing.
function RevisionTuneCTA({ datalog, wotAvgAfr, avgAfr }) {
  // Need a detected tune version so we know what bin to derive from.
  const version = datalog?.tune_version || datalog?.detected_from;
  const baseFilename = version && VERSION_TO_FILENAME[version];
  // Prefer the WOT band average (closer to peak load); fall back to overall.
  const observedAfr = num(wotAvgAfr) ?? num(avgAfr);
  if (!baseFilename || observedAfr === null || observedAfr <= 0) return null;

  const targetAfr = TARGET_AFR_BY_VERSION[version] || 11.5;
  const scaleFactor = observedAfr / targetAfr;
  const deltaPct = (scaleFactor - 1) * 100;
  const directionLabel = deltaPct > 0 ? 'richer' : 'leaner';

  // Skip rendering if the correction is trivially small — no point in a
  // round-trip flash for a < 1% pulsewidth change.
  if (Math.abs(deltaPct) < 1.0) {
    return (
      <Card className="bg-emerald-500/5 border-emerald-500/30 rounded-sm" data-testid="revision-tune-cta-on-target">
        <CardContent className="p-4 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <div>
            <p className="text-sm text-white font-['Outfit'] font-medium">AFR is on target — no revision tune needed</p>
            <p className="text-xs text-zinc-500">
              Observed AFR {observedAfr.toFixed(2)} vs target {targetAfr.toFixed(2)}. Re-run after another pull to confirm.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleClick = () => {
    const detail = {
      base_filename: baseFilename,
      mods: [{ id: 'afr_correction', params: { target_afr: targetAfr, actual_afr: observedAfr } }],
      sourceLabel: `${version} datalog "${datalog.filename || 'log'}"`,
    };
    window.dispatchEvent(new CustomEvent('open-derive-from-analysis', { detail }));
  };

  return (
    <Card className="bg-[#FF5E1A]/5 border-[#FF5E1A]/30 rounded-sm" data-testid="revision-tune-cta">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Wrench className="w-4 h-4 text-[#FF5E1A]" />
              <p className="text-sm text-white font-['Outfit'] font-bold">Generate Revision Tune</p>
              <span className="text-[9px] text-[#FF5E1A] font-['JetBrains_Mono'] bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 px-1.5 py-0.5 rounded-sm">
                from {version}
              </span>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Datalog AFR <span className="text-white font-bold font-['JetBrains_Mono']">{observedAfr.toFixed(2)}</span>
              {' vs target '}
              <span className="text-white font-bold font-['JetBrains_Mono']">{targetAfr.toFixed(2)}</span>.
              {' Scale fuel × '}
              <span className="text-white font-bold font-['JetBrains_Mono']">{scaleFactor.toFixed(3)}</span>
              {' '}({deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}% — {directionLabel}).
            </p>
          </div>
          <Button
            onClick={handleClick}
            className="bg-gradient-to-r from-[#FF5E1A] to-amber-500 hover:from-[#FF7A3D] hover:to-amber-400 text-white text-xs font-bold rounded-sm shadow-[0_0_16px_rgba(255,94,26,0.4)] flex-shrink-0"
            data-testid="generate-revision-tune-btn"
          >
            <Wrench className="w-3.5 h-3.5 mr-1.5" />Generate Revision Tune
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Short-datalog threshold: <15s or <50 rows can't produce meaningful WOT stats.
const SHORT_DATALOG_ROW_THRESHOLD = 50;
const SHORT_DATALOG_SEC_THRESHOLD = 15;

export default function DatalogAnalysisView({ datalog }) {
  if (!datalog) return null;
  const analysis = datalog.analysis || {};
  const rawStats = datalog.raw_stats || {};

  // Remap to the real backend schema (see /app/backend/datalog_analysis.py).
  const dataPoints = datalog.data_points ?? analysis.total_rows;
  const durationSec = datalog.duration_seconds ?? analysis.duration_sec;
  const maxRpm = rawStats?.rpm?.max ?? analysis.max_rpm;
  const avgAfr = rawStats?.afr_b1?.avg ?? analysis.avg_afr;
  const hp = analysis.hp || analysis.hp_estimate || {};
  const wot = analysis.wot || analysis.wot_analysis || {};
  const issues = Array.isArray(datalog.issues) ? datalog.issues
    : Array.isArray(analysis.issues) ? analysis.issues : [];
  const recs = Array.isArray(datalog.recommendations) ? datalog.recommendations
    : Array.isArray(analysis.recommendations) ? analysis.recommendations : [];

  const peakHp = hp.estimated_peak_hp ?? hp.peak_hp ?? hp.peak_hp_crank;
  const peakHpRpm = hp.peak_hp_rpm;
  const peakTorque = hp.estimated_peak_torque ?? hp.peak_torque;
  const peakTorqueRpm = hp.peak_torque_rpm;

  const wotBands = Object.entries(wot).map(([range, d]) => ({
    range,
    afr: num(d?.afr),
    timing: num(d?.timing),
    torque: num(d?.torque) ?? num(d?.max_torque) ?? num(d?.peak_torque),
    status: d?.afr_status || d?.status || 'NORMAL',
  }));

  const wotAvgAfr = wotBands.length
    ? wotBands.reduce((a, b) => a + (b.afr || 0), 0) / wotBands.filter(b => b.afr !== null).length
    : null;
  const wotAvgTiming = wotBands.length
    ? wotBands.reduce((a, b) => a + (b.timing || 0), 0) / wotBands.filter(b => b.timing !== null).length
    : null;

  const isShort = (num(dataPoints) !== null && dataPoints < SHORT_DATALOG_ROW_THRESHOLD)
    || (num(durationSec) !== null && durationSec < SHORT_DATALOG_SEC_THRESHOLD);

  return (
    <div className="space-y-6">
      {/* AI Insights — top of analysis. Wrapped so a library-level clone error
          (recharts/react-markdown/etc) can't blank out the whole view. */}
      <PanelErrorBoundary label="AI insights could not be generated">
        <AIInsightsPanel datalog={datalog} />
      </PanelErrorBoundary>

      {/* Generate Revision Tune — turns the AI's analysis into a one-click
          derived tune. Dispatches a window event that App.js + TuneTimeline
          handle: switch to My Tunes tab and open the Derive dialog with the
          AFR correction prefilled. Only shows when we have both a detected
          tune version (so we know the base) AND an AFR signal. */}
      <RevisionTuneCTA datalog={datalog} wotAvgAfr={wotAvgAfr} avgAfr={avgAfr} />

      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Activity className="w-5 h-5 text-[#FF5E1A]" />
            Analysis: {datalog.filename || 'datalog.csv'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Short-datalog advisory — a 7-second / 67-row log won't have WOT
              or RPM band stats. Tell the user instead of leaving a wall of N/A. */}
          {isShort && (
            <Alert className="mb-4 bg-yellow-500/10 border-yellow-500/30 rounded-sm" data-testid="short-datalog-banner">
              <Info className="h-4 w-4 text-yellow-400" />
              <AlertTitle className="text-white font-['Outfit']">Datalog too short for full analysis</AlertTitle>
              <AlertDescription className="text-zinc-400 text-sm">
                This log has {num(dataPoints) !== null ? `${dataPoints} rows` : 'very few rows'}
                {num(durationSec) !== null ? ` over ${fmt(durationSec, 1)}s` : ''}.
                WOT, HP and RPM-band metrics need a full 3rd‑gear pull (15s+). Record a clean pull
                from idle → redline with throttle held wide open, then re-upload.
              </AlertDescription>
            </Alert>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Rows" value={num(dataPoints) !== null ? dataPoints : 'N/A'} />
            <StatCard label="Duration" value={num(durationSec) !== null ? `${fmt(durationSec, 0)}s` : 'N/A'} />
            <StatCard label="Max RPM" value={fmt(maxRpm, 0)} unit="RPM" />
            <StatCard label="Avg AFR" value={fmt(avgAfr, 2)} unit="AFR" />
          </div>

          {/* Recommendations summary */}
          {recs.length > 0 && (
            <Alert className="rounded-sm bg-emerald-500/10 border-emerald-500/30">
              <AlertTriangle className="h-4 w-4 text-emerald-400" />
              <AlertTitle className="text-white font-['Outfit']">Recommendations</AlertTitle>
              <AlertDescription className="text-zinc-400 text-sm">
                {recs.join(' • ')}
              </AlertDescription>
            </Alert>
          )}

          {/* HP/Torque Estimates — only render when we actually measured something. */}
          {((num(peakHp) || 0) > 0 || (num(peakTorque) || 0) > 0) && (
            <div className="grid grid-cols-2 gap-4 mt-6">
              <Card className="bg-[#050505] border-[#27272A] rounded-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Est. Peak HP</p>
                  <p className="text-4xl font-bold text-[#FF5E1A] font-['JetBrains_Mono']">{fmt(peakHp, 1)}</p>
                  <p className="text-xs text-zinc-500">@ {fmt(peakHpRpm, 0)} RPM</p>
                </CardContent>
              </Card>
              <Card className="bg-[#050505] border-[#27272A] rounded-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Est. Peak Torque</p>
                  <p className="text-4xl font-bold text-[#FF5E1A] font-['JetBrains_Mono']">{fmt(peakTorque, 1)}</p>
                  <p className="text-xs text-zinc-500">ft-lbs @ {fmt(peakTorqueRpm, 0)} RPM</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* WOT Analysis (aggregate) */}
          {wotBands.length > 0 && (
            <div className="mt-6">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">WOT Analysis</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="WOT AFR" value={fmt(wotAvgAfr, 2)} unit="AFR" />
                <StatCard label="WOT Timing" value={fmt(wotAvgTiming, 1)} unit="deg" />
                <StatCard label="Bands" value={wotBands.length} />
                <StatCard label="Max RPM" value={fmt(maxRpm, 0)} unit="RPM" />
              </div>
            </div>
          )}

          {/* WOT Band Breakdown */}
          {wotBands.length > 0 && (
            <div className="mt-6">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">RPM Band Breakdown</p>
              <div className="space-y-2">
                {wotBands.map((band) => (
                  <div key={band.range} className="flex items-center gap-4 p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                    <span className="text-xs text-zinc-500 w-24 font-['JetBrains_Mono']">{band.range}</span>
                    <div className="flex-1">
                      <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${getAFRBarColor(band.afr || 0)}`} style={{ width: `${Math.min(100, ((band.afr || 0) / 20) * 100)}%` }} />
                      </div>
                    </div>
                    <span className="text-xs text-white w-16 text-right font-['JetBrains_Mono']">{fmt(band.afr, 2)}</span>
                    <AFRStatusBadge status={band.status} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Issues — normalize dict → message string so we never render an
              object as a React child. Prior code did `{issue}` where issue
              is `{type, message, severity, ...}` from the backend, which
              triggers a render-time failure on some React builds. */}
          {issues.length > 0 && (
            <div className="mt-6">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Issues Detected</p>
              <div className="space-y-2">
                {issues.map((issue, idx) => (
                  <Alert key={issueKey(issue, idx)} className="bg-red-500/5 border-red-500/20 rounded-sm">
                    <CircleAlert className="h-4 w-4 text-red-400" />
                    <AlertDescription className="text-zinc-300 text-sm">{issueText(issue)}</AlertDescription>
                  </Alert>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
